<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Routes from '@/constants/Routes';
import { t } from '@/i18n';
import { DxLoadIndicator } from 'devextreme-vue/load-indicator';
import { useAlertStore } from '@/services/stores/alertStore';
import { useRoute } from 'vue-router';
import type { IEmailConfirmRequest } from '@/types/interfaces/LoginTypes.ts';
import { useAccountService } from '@/services/api/accountService';

const account = useAccountService();
const alert = useAlertStore();
const route = useRoute();
const loading = ref<boolean>(true);
const success = ref<boolean | null>(null);
const emailDetails = ref<IEmailConfirmRequest>({
  emailAddress: route.query.email as string || '',
  token: route.query.token as string || ''
});

definePage({
  name: 'LoginConfirmEmail',
  meta: {
    anonymous: true
  }
});

function validate(): boolean {
  const errors: Array<string> = [];

  if (emailDetails.value.emailAddress === '') {
    errors.push(t('login.emailMissing'));
  }

  if (emailDetails.value.token === '') {
    errors.push(t('login.confirmTokenMissing'));
  }

  alert.showWarning(null, errors);
  return errors.length === 0;
}

onMounted(async () => {
  if (!validate()) {
    loading.value = false;
    return;
  }

  const response = await account.confirmEmail(emailDetails.value);
  if (!response.success) {
    alert.showError(response.problem?.detail ?? t('login.confirmEmailFailed'));
  }

  success.value = response.success;
  loading.value = false;
});
</script>

<template>
  <p v-if="success === null || success" class="lead text-center">
    {{ $t('login.confirmingEmail') }}
  </p>

  <div class="row justify-content-center" v-if="loading">
    <div class="col-auto">
      <DxLoadIndicator :visible="true" height="48px" width="48px" />
    </div>
  </div>

  <div v-if="!loading" class="row">
    <div class="col text-center">
      <p class="mt-2">
        <i18n-t v-if="success" keypath="login.emailVerified" scope="global">
          <template #link><RouterLink :to="{ name: Routes.Login.Root }">{{ $t('login.emailVerifiedLink') }}</RouterLink></template>
        </i18n-t>
        <i18n-t v-else keypath="login.newVerificationEmail" scope="global">
          <template #link><RouterLink :to="{ name: Routes.Login.ForgotPassword }">{{ $t('login.newVerificationEmailLink') }}</RouterLink></template>
        </i18n-t>
      </p>
    </div>
  </div>

</template>

<style scoped>

</style>